// client/src/components/configuracoes/plataformaGoogleLogin/PlataformaGoogleLoginConfigPage.js
// Client ID do "Entrar com Google" pro cliente final (site/conta). Cada
// Conta só mostra o botão se o PRÓPRIO plano tiver a feature hasLoginGoogle
// (Planos de Conta) — esta tela liga a plataforma inteira, o plano libera
// por Conta. Espelha PlataformaGoogleNegocioConfigPage.js, mais simples
// (sem Client Secret nem URL de callback — Google Identity Services só
// precisa do Client ID, que é um valor público).
import React, { useState, useEffect, useCallback } from 'react';
import { LogIn, Save, Info } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { usePlataformaGoogleLoginConfig, useSalvarPlataformaGoogleLoginConfig } from '../../../hooks/usePlataformaGoogleLogin';

const ToggleSwitch = ({ checked, onChange, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
        <div
            onClick={() => onChange(!checked)}
            style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                background: checked ? 'var(--color-primary)' : '#d1d5db',
                transition: 'background 0.2s',
            }}
        >
            <div style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                left: checked ? 22 : 2,
                transition: 'left 0.2s',
            }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</span>
    </label>
);

const PlataformaGoogleLoginConfigPage = () => {
    const { data: config, isLoading } = usePlataformaGoogleLoginConfig();
    const salvarConfig = useSalvarPlataformaGoogleLoginConfig();

    const [clientId, setClientId] = useState('');
    const [ativo, setAtivo] = useState(false);

    useEffect(() => {
        if (config) {
            setClientId(config.clientId || '');
            setAtivo(config.ativo ?? false);
        }
    }, [config]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        salvarConfig.mutate({ clientId, ativo });
    }, [clientId, ativo, salvarConfig]);

    if (isLoading) return null;

    return (
        <div className="page-management-container">
            <PageHeader icon={<LogIn />} title="Login com Google" subtitle="Cliente final entrando pelo site com a própria conta Google" />
            <form onSubmit={handleSubmit}>
                <div className="content-panel">
                    <div className="form-section">
                        <h4 className="form-section-title">Status</h4>
                        <div className="form-group">
                            <ToggleSwitch checked={ativo} onChange={setAtivo} label={ativo ? 'Integração ativa' : 'Integração desativada'} />
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Ativar aqui só habilita a plataforma — cada Conta só mostra o botão "Entrar com Google" se o próprio plano tiver essa feature vinculada (Planos de Conta → Features).
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Credencial OAuth</h4>
                        <div className="form-group">
                            <label className="form-label required">Client ID</label>
                            <input className="form-input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxxxxxxx.apps.googleusercontent.com" />
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Pode ser um Client ID diferente do usado em Google Meu Negócio (é outro produto do Google) — crie um em console.cloud.google.com, tipo "Aplicativo da Web", sem Client Secret. Cadastre o domínio do site/conta em "Origens JavaScript autorizadas".
                        </p>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="action-button action-button-primary" disabled={salvarConfig.isPending}>
                            <Save size={18} className="icon" /> {salvarConfig.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PlataformaGoogleLoginConfigPage;
